import React from 'react';
import { Stack, Group, Title, Text, Card, SimpleGrid, Skeleton, Grid } from '@mantine/core';
import { IconBuildings, IconDeviceTv, IconDevicesPc, IconSunrise, IconSun, IconMoonStars } from '@tabler/icons-react';
import useSWR from 'swr';
import { AxiosError } from 'axios';
import { organizationSummaryFetcher, OrganizationSummary } from '../data/Organization';
import { listLocationFetcher } from '../data/Location';
import type { Location } from '../data/Location';
import { PingSummary, pingSummaryFetcher } from '../data/PingSummary';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import DeviceMap from './DeviceMap';
import PingKpiStrip from './home/PingKpiStrip';
import SportMixDonut from './home/SportMixDonut';
import Activity24hChart from './home/Activity24hChart';
import StaleDevicesList from './home/StaleDevicesList';

const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
        return {
            text: 'Good Morning',
            icon: <IconSunrise size={36} stroke={1.5} className="greeting-icon greeting-icon-sunrise" />,
        };
    }
    if (hour < 17) {
        return {
            text: 'Good Afternoon',
            icon: <IconSun size={36} stroke={1.5} className="greeting-icon greeting-icon-sun" />,
        };
    }
    return {
        text: 'Good Evening',
        icon: <IconMoonStars size={36} stroke={1.5} className="greeting-icon greeting-icon-evening" />,
    };
};

const Home: React.FC = () => {
    const { activeOrg } = useAuth();
    const navigate = useNavigate();

    // The active org, not the user's home org: staff switched into a customer
    // must see that customer's counts, and the endpoint rejects any id that
    // isn't the active org anyway.
    const { data: organization } = useSWR<OrganizationSummary, AxiosError>(
        activeOrg ? `api/organization/${activeOrg.id}/summary` : null,
        organizationSummaryFetcher,
    );

    const { data: locations, isLoading: locationsLoading } = useSWR<Location[], AxiosError>(
        'api/locations',
        listLocationFetcher,
    );

    const { data: pingSummary } = useSWR<PingSummary, AxiosError>(
        'api/home/ping-summary',
        pingSummaryFetcher,
        { refreshInterval: 10_000 },
    );

    const stats = [
        {
            label: 'Locations',
            value: organization?.counts?.locationCount ?? '—',
            icon: <IconBuildings size="1.2rem" stroke={1.5} />,
            route: '/locations',
        },
        {
            label: 'Device Tags',
            value: organization?.counts?.deviceTagCount ?? '—',
            icon: <IconDeviceTv size="1.2rem" stroke={1.5} />,
            route: '/device-tags',
        },
        {
            label: 'Devices',
            value: organization?.counts?.deviceCount ?? '—',
            icon: <IconDevicesPc size="1.2rem" stroke={1.5} />,
            route: '/devices',
        },
    ];

    return (
        <Stack gap="md">
            {(() => {
                const g = greeting();
                return (
                    <Group gap="sm" align="center">
                        {g.icon}
                        <Title order={2}>{g.text}</Title>
                    </Group>
                );
            })()}

            <Stack gap="xs" mt="md">
                <Group gap="xs" align="center">
                    <span className="live-dot" aria-label="live" />
                    <Title order={4}>Live status</Title>
                </Group>
                <Grid gutter="md">
                    <Grid.Col span={{ base: 12, md: 5 }}>
                        <PingKpiStrip data={pingSummary?.live} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 7 }}>
                        {locationsLoading ? (
                            <Skeleton height={270} radius="sm" />
                        ) : (
                            <DeviceMap locations={locations ?? []} height={270} />
                        )}
                    </Grid.Col>
                </Grid>
            </Stack>

            <Stack gap="xs" mt="md">
                <Title order={4}>Today's activity</Title>
                <Grid gutter="md">
                    <Grid.Col span={{ base: 12, md: 7 }}>
                        <Activity24hChart data={pingSummary?.activityLast24h} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 5 }}>
                        <SportMixDonut data={pingSummary?.sportMixToday} />
                    </Grid.Col>
                </Grid>
            </Stack>

            <StaleDevicesList data={pingSummary?.staleDevices} />

            <SimpleGrid cols={{ base: 1, sm: 3 }}>
                {stats.map(stat => (
                    <Card
                        key={stat.label}
                        shadow="xs"
                        withBorder
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(stat.route)}
                    >
                        <Group gap="sm">
                            {stat.icon}
                            <div>
                                <Text fw={700} size="xl" lh={1}>{stat.value}</Text>
                                <Text size="sm" c="dimmed">{stat.label}</Text>
                            </div>
                        </Group>
                    </Card>
                ))}
            </SimpleGrid>
        </Stack>
    );
};

export default Home;
